import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SidebarItem from './SidebarItem';
import UserProfile from './UserProfile';
import { 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Home,
  MessageCircle,
  Settings as SettingsIcon,
  Clock,
  Link
} from 'lucide-react';

const SidebarContainer = styled(motion.div)`
  width: ${({ $isOpen }) => $isOpen ? '280px' : '60px'};
  height: calc(100vh - 32px);
  background: ${({ theme }) => theme.colors.dark.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.dark.border};
  display: flex;
  flex-direction: column;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  // margin-top: 32px;
`;

const SidebarHeader = styled.div`
  height: 60px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  min-width: 36px;
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::before {
    content: '🤖';
    font-size: 1.25rem;
  }
`;

const LogoText = styled(motion.div)`
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
`;

const LogoTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
  line-height: 1.2;
`;

const LogoSubtitle = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1;
`;

const ToggleButton = styled.button`
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  pointer-events: ${({ $isOpen }) => $isOpen ? 'auto' : 'none'};
  
  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
  
  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const CollapsedToggleButton = styled.button`
  position: absolute;
  top: 16px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: ${({ $isOpen }) => $isOpen ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: ${({ theme }) => theme.colors.dark.elevated};
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
  
  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const Navigation = styled.nav`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.dark.border};
    border-radius: 2px;
  }
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SidebarFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.dark.border};
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const UserProfileClickable = styled.div`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.dark.bg};
  }
`;

const Sidebar = ({ isOpen, onToggle, activeSection, onSectionChange }) => {
  const { user, signOut } = useAuth();

  // Main navigation items
  const mainNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'conversations', label: 'Conversations', icon: MessageCircle },
    { id: 'reminders', label: 'Reminders', icon: Clock },
    { id: 'connections', label: 'Connections', icon: Link }
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleUserProfileClick = () => {
    onSectionChange('user-settings');
  };

  return (
    <>
      <SidebarContainer $isOpen={isOpen}>
        <CollapsedToggleButton $isOpen={isOpen} onClick={onToggle}>
          <ChevronRight />
        </CollapsedToggleButton>
        
        <SidebarHeader>
          <LogoSection>
            <LogoIcon />
            <AnimatePresence>
              {isOpen && (
                <LogoText
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <LogoTitle>SARVIK</LogoTitle>
                  <LogoSubtitle>Assistant</LogoSubtitle>
                </LogoText>
              )}
            </AnimatePresence>
          </LogoSection>
          
          <ToggleButton $isOpen={isOpen} onClick={onToggle}>
            <ChevronLeft />
          </ToggleButton>
        </SidebarHeader>

        <Navigation>
          <NavList>
            {mainNavItems.map((item) => (
              <SidebarItem
                key={item.id}
                {...item}
                isActive={activeSection === item.id}
                isCollapsed={!isOpen}
                onClick={() => onSectionChange(item.id)}
              />
            ))}
          </NavList>
        </Navigation>

        <SidebarFooter>
          <UserProfile 
            user={user} 
            isCollapsed={!isOpen}
            onClick={handleUserProfileClick}
          />
          
          <SidebarItem
            id="logout"
            label="Sign Out"
            icon={LogOut}
            isCollapsed={!isOpen}
            onClick={handleSignOut}
            variant="danger"
          />
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;